import React, { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ExternalLink, Loader2, Save, Send, Server, Settings2
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  communicationsService,
  emailIsValid,
  mailDiagnosticFromError
} from '../../services/communicationsService'
import type { Locale, MailDiagnostic, MailProvider, MailSettings } from '../../types/communications'

interface Props {
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const EMPTY: MailSettings = {
  host: '',
  port: 587,
  secure: false,
  username: '',
  password: '',
  passwordConfigured: false,
  maskedPassword: '',
  fromName: 'Sini Fleet',
  fromAddress: 'no-reply@fleet.sinigroupe.com',
  replyTo: '',
  configured: false,
  status: ''
}

type Step = 0 | 1 | 2

const SmtpSettingsPanel: React.FC<Props> = ({ notify }) => {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as Locale
  const [settings, setSettings] = useState<MailSettings>(EMPTY)
  const [providers, setProviders] = useState<MailProvider[]>([])
  const [providerKey, setProviderKey] = useState('')
  const [step, setStep] = useState<Step>(0)
  const [advanced, setAdvanced] = useState(false)
  const [recipient, setRecipient] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [diagnostic, setDiagnostic] = useState<MailDiagnostic | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    Promise.all([
      communicationsService.getMailSettings().catch(error => {
        setDiagnostic(mailDiagnosticFromError(error))
        return EMPTY
      }),
      communicationsService.getMailProviders(locale).catch(() => [] as MailProvider[])
    ]).then(([saved, catalog]) => {
      setSettings(saved)
      setProviders(catalog)
      // Recognise the provider already in use so the guide reflects reality.
      const match = catalog.find(item => item.host && item.host === saved.host)
      setProviderKey(match?.key || (saved.host ? 'CUSTOM' : ''))
      if (saved.configured) setStep(2)
    }).finally(() => setLoading(false))
  }, [])

  const provider = useMemo(() => providers.find(item => item.key === providerKey) || null, [providers, providerKey])

  const update = <K extends keyof MailSettings>(key: K, value: MailSettings[K]) => {
    setSettings(current => ({ ...current, [key]: value }))
    setFieldErrors(current => {
      const next = { ...current }
      delete next[key as string]
      return next
    })
  }

  const chooseProvider = (next: MailProvider) => {
    setProviderKey(next.key)
    setSettings(current => ({
      ...current,
      host: next.host || (next.key === 'CUSTOM' ? current.host : ''),
      port: next.port,
      secure: next.secure,
      username: next.usernameIs === 'FIXED' ? next.fixedUsername || '' : current.username
    }))
    setDiagnostic(null)
  }

  // Each step is checked before moving on, so nothing fails only at the end.
  const stepErrors = (target: Step): Record<string, string> => {
    const errors: Record<string, string> = {}
    if (target === 0) {
      if (!providerKey) errors.provider = t('communications.smtp.chooseProvider')
      if (!settings.host.trim()) errors.host = t('communications.validation.host')
      if (!Number.isInteger(settings.port) || settings.port < 1 || settings.port > 65535) {
        errors.port = t('communications.validation.port')
      }
    }
    if (target === 1) {
      if (settings.username && !settings.passwordConfigured && !settings.password) {
        errors.password = t('communications.validation.smtpPassword')
      }
    }
    if (target === 2) {
      if (!emailIsValid(settings.fromAddress)) errors.fromAddress = t('communications.validation.email')
      if (settings.replyTo && !emailIsValid(settings.replyTo)) errors.replyTo = t('communications.validation.email')
    }
    return errors
  }

  const advance = () => {
    const errors = stepErrors(step)
    setFieldErrors(errors)
    if (Object.keys(errors).length === 0) setStep(current => Math.min(2, current + 1) as Step)
  }

  const save = async () => {
    const errors = { ...stepErrors(0), ...stepErrors(1), ...stepErrors(2) }
    setFieldErrors(errors)
    if (Object.keys(errors).length) {
      notify?.(t('communications.smtp.fixFields'), 'warning')
      return
    }
    setSaving(true)
    setDiagnostic(null)
    try {
      setSettings(await communicationsService.saveMailSettings(settings))
      notify?.(t('communications.smtp.saved'), 'success')
    } catch (error) {
      const next = mailDiagnosticFromError(error)
      setDiagnostic(next)
      if (next.field) setFieldErrors(current => ({ ...current, [next.field as string]: next.message }))
    } finally {
      setSaving(false)
    }
  }

  const test = async () => {
    if (!emailIsValid(recipient)) {
      setFieldErrors(current => ({ ...current, to: t('communications.validation.email') }))
      return
    }
    setTesting(true)
    setDiagnostic(null)
    try {
      await communicationsService.testMail(recipient, locale)
      notify?.(t('communications.smtp.testSent'), 'success')
    } catch (error) {
      setDiagnostic(mailDiagnosticFromError(error))
    } finally {
      setTesting(false)
    }
  }

  if (loading) return <div className="flex h-48 items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>

  const inputClass = (field: string) =>
    `mt-1.5 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-100 ${
      fieldErrors[field] ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
    }`
  const error = (field: string) => fieldErrors[field]
    ? <span className="mt-1 block text-[10px] text-red-600">{fieldErrors[field]}</span>
    : null

  const steps = [
    t('communications.smtp.steps.provider'),
    t('communications.smtp.steps.credentials'),
    t('communications.smtp.steps.sender')
  ]

  return <div className="space-y-4">
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${
      settings.configured ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
    }`}>
      <div className="flex items-center gap-3">
        <Server size={21} className={settings.configured ? 'text-emerald-600' : 'text-amber-600'} />
        <div>
          <div className="text-sm font-semibold">
            {t(settings.configured ? 'communications.smtp.configured' : 'communications.smtp.notConfigured')}
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            {settings.configured
              ? `${settings.fromAddress}${settings.updatedAt ? ` · ${t('communications.smtp.updated')} ${new Date(settings.updatedAt).toLocaleString()}` : ''}`
              : t('communications.smtp.completeSettings')}
          </div>
        </div>
      </div>
    </div>

    {diagnostic && <div className="rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
      <div className="flex gap-3">
        <AlertTriangle className="mt-0.5 shrink-0 text-red-600" size={18} />
        <div>
          <div className="text-sm font-semibold text-red-900">
            {t(`communications.mailErrors.${diagnostic.code}.title`, { defaultValue: diagnostic.title })}
          </div>
          <p className="mt-1 text-xs text-red-800">
            {t(`communications.mailErrors.${diagnostic.code}.message`, { defaultValue: diagnostic.message })}
          </p>
          {diagnostic.suggestions.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-red-700">
            {diagnostic.suggestions.map((suggestion, index) => <li key={index}>{suggestion}</li>)}
          </ul>}
        </div>
      </div>
    </div>}

    <nav className="flex flex-wrap items-center gap-2">
      {steps.map((label, index) => <button key={label} onClick={() => setStep(index as Step)}
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold ${
          step === index ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
        }`}>
        <span className={`grid h-4 w-4 place-items-center rounded-full text-[9px] ${
          step === index ? 'bg-white text-blue-700' : 'bg-white text-gray-500'
        }`}>{index + 1}</span>
        {label}
      </button>)}
    </nav>

    {step === 0 && <section className="space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {providers.map(item => <button key={item.key} onClick={() => chooseProvider(item)}
          className={`rounded-xl border p-3 text-left ${
            providerKey === item.key ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
          }`}>
          <span className="block text-xs font-semibold text-gray-800">{item.name}</span>
          <span className="mt-0.5 block text-[10px] text-gray-500">
            {item.host || t('communications.smtp.ownServer')}
            {item.host && ` · ${item.port}`}
          </span>
        </button>)}
      </div>
      {error('provider')}

      {provider && <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
        <div className="text-xs font-semibold text-slate-800">{t('communications.smtp.beforeYouStart')}</div>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] text-slate-700">
          {provider.requirements.map((requirement, index) => <li key={index}>{requirement}</li>)}
        </ul>
        {provider.docsUrl && <a href={provider.docsUrl} target="_blank" rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
          {t('communications.smtp.providerDocs')}<ExternalLink size={11} />
        </a>}
      </div>}

      <button onClick={() => setAdvanced(!advanced)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-600">
        <Settings2 size={12} />{t(advanced ? 'communications.smtp.hideAdvanced' : 'communications.smtp.showAdvanced')}
      </button>

      {(advanced || providerKey === 'CUSTOM') && <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600 sm:col-span-2">
          {t('communications.smtp.host')}
          <input value={settings.host} onChange={event => update('host', event.target.value)} className={inputClass('host')} />
          {error('host')}
        </label>
        <label className="rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600">
          {t('communications.smtp.port')}
          <input type="number" min={1} max={65535} value={settings.port}
            onChange={event => update('port', Number(event.target.value))} className={inputClass('port')} />
          {error('port')}
        </label>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 text-sm sm:col-span-3">
          <span>
            <span className="block font-medium text-gray-800">{t('communications.smtp.implicitTls')}</span>
            <span className="text-[10px] text-gray-500">{t('communications.smtp.tlsHint')}</span>
          </span>
          <input type="checkbox" checked={settings.secure} onChange={event => update('secure', event.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600" />
        </label>
      </div>}
    </section>}

    {step === 1 && <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label className="rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600">
        {t('communications.smtp.username')}
        <input value={settings.username} onChange={event => update('username', event.target.value)}
          readOnly={provider?.usernameIs === 'FIXED'} autoComplete="username" className={inputClass('username')} />
        <span className="mt-1 block text-[10px] font-normal text-gray-500">
          {t(provider?.usernameIs === 'FIXED'
            ? 'communications.smtp.usernameFixed'
            : provider?.usernameIs === 'EMAIL'
              ? 'communications.smtp.usernameEmail'
              : 'communications.smtp.usernameLogin')}
        </span>
      </label>
      <label className="rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600">
        {settings.passwordConfigured ? t('communications.smtp.replacePassword') : t('communications.smtp.password')}
        <input type="password" value={settings.password} onChange={event => update('password', event.target.value)}
          autoComplete="new-password" placeholder={settings.passwordConfigured ? t('communications.smtp.keepPassword') : ''}
          className={inputClass('password')} />
        {error('password')}
      </label>
    </section>}

    {step === 2 && <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label className="rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600">
        {t('communications.smtp.senderName')}
        <input value={settings.fromName} onChange={event => update('fromName', event.target.value)} className={inputClass('fromName')} />
        <span className="mt-1 block text-[10px] font-normal text-gray-500">{t('communications.smtp.senderNameHint')}</span>
      </label>
      <label className="rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600">
        {t('communications.smtp.senderAddress')}
        <input type="email" value={settings.fromAddress} onChange={event => update('fromAddress', event.target.value)}
          className={inputClass('fromAddress')} />
        {error('fromAddress')}
      </label>
      <label className="rounded-xl border border-gray-200 bg-white p-3 text-xs font-medium text-gray-600 sm:col-span-2">
        {t('communications.smtp.replyTo')}
        <input type="email" value={settings.replyTo} onChange={event => update('replyTo', event.target.value)}
          className={inputClass('replyTo')} />
        <span className="mt-1 block text-[10px] font-normal text-gray-500">{t('communications.smtp.replyToHint')}</span>
        {error('replyTo')}
      </label>
    </section>}

    <div className="flex flex-wrap items-center justify-between gap-2">
      <button onClick={() => setStep(current => Math.max(0, current - 1) as Step)} disabled={step === 0}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-40">
        <ArrowLeft size={13} />{t('communications.smtp.back')}
      </button>
      {step < 2
        ? <button onClick={advance}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white">
          {t('communications.smtp.next')}<ArrowRight size={13} />
        </button>
        : <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {t('communications.smtp.saveVerify')}
        </button>}
    </div>

    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <Send size={15} className="text-blue-600" />
        <h3 className="text-sm font-semibold">{t('communications.smtp.sendTest')}</h3>
      </div>
      <p className="mt-1 text-xs text-gray-500">{t('communications.smtp.testHint')}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <div className="min-w-0 flex-1">
          <input type="email" value={recipient} onChange={event => {
            setRecipient(event.target.value)
            setFieldErrors(current => ({ ...current, to: '' }))
          }} placeholder="recipient@example.com" className={inputClass('to')} />
          {error('to')}
        </div>
        <button onClick={test} disabled={testing || !settings.configured}
          className="inline-flex items-center justify-center gap-2 self-start rounded-lg bg-slate-800 px-4 py-2.5 text-xs font-medium text-white disabled:opacity-50">
          {testing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
          {t('communications.smtp.verifyDelivery')}
        </button>
      </div>
    </section>
  </div>
}

export default SmtpSettingsPanel
